import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dices, RefreshCw, Plus, Trash2, Trophy, Settings } from 'lucide-react';
import { Card, Button, Input } from '../ui';
import { cn } from '../../lib/utils';

const COLORS = [
  '#ef4444', // red
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f97316', // orange
];

export function VirtualPrizeWheel() {
  const [activeTab, setActiveTab] = useState<'dice' | 'wheel'>('dice');

  // --- DICE ROLLER STATE ---
  const [numDice, setNumDice] = useState<number>(2);
  const [diceValues, setDiceValues] = useState<number[]>([6, 6]);
  const [isRolling, setIsRolling] = useState(false);

  // --- WHEEL STATE ---
  const [wheelItems, setWheelItems] = useState<{id: string, text: string}[]>([
    { id: '1', text: 'Free Coffee' },
    { id: '2', text: 'Front Row Seat' },
    { id: '3', text: 'Candy Bar' },
    { id: '4', text: 'Skip an Activity' },
    { id: '5', text: 'Bingo Extra Card' },
    { id: '6', text: 'Gift Shop $5' }
  ]);
  const [newItemText, setNewItemText] = useState('');
  const [rotation, setRotation] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const [winner, setWinner] = useState<string | null>(null);

  // --- DICE LOGIC ---
  const rollDice = () => {
    if (isRolling) return;
    setIsRolling(true);
    setWinner(null);
    
    // Simulate rolling animation
    let rolls = 0;
    const interval = setInterval(() => {
      setDiceValues(Array.from({ length: numDice }, () => Math.floor(Math.random() * 6) + 1));
      rolls++;
      if (rolls > 15) {
        clearInterval(interval);
        setIsRolling(false);
      }
    }, 50);
  };

  // Reset dice values if number of dice changes (render-phase adjustment).
  const [prevNumDice, setPrevNumDice] = useState(numDice);
  if (numDice !== prevNumDice) {
    setPrevNumDice(numDice);
    setDiceValues(Array.from({ length: numDice }, () => 6));
  }

  // --- WHEEL LOGIC ---
  const addWheelItem = () => {
    if (!newItemText.trim()) return;
    setWheelItems([...wheelItems, { id: Date.now().toString(), text: newItemText.trim() }]);
    setNewItemText('');
  };

  const removeWheelItem = (id: string) => {
    if (wheelItems.length <= 2) return; // Min 2 items
    setWheelItems(wheelItems.filter(i => i.id !== id));
  };

  const spinWheel = () => {
    if (isSpinning || wheelItems.length === 0) return;
    setIsSpinning(true);
    setWinner(null);

    // Calculate rotation: spin at least 5 full times (1800 deg) + random stop
    const extraSpins = 5 * 360;
    const randomDegree = Math.floor(Math.random() * 360);
    const newRotation = rotation + extraSpins + randomDegree;
    
    setRotation(newRotation);

    // Calculate winner after animation
    setTimeout(() => {
      setIsSpinning(false);
      // Actual rotation modulo 360 to find the slice
      const normalizedRotation = newRotation % 360;
      const sliceAngle = 360 / wheelItems.length;
      
      // Pointer is at the top (270 degrees in SVG context or 0 degrees visually based on implementation)
      // Our SVG starts slice 0 at the top (rotated -90deg or handled by path)
      // We will map the top position
      // For simplicity, determine index based on rotation
      const winningIndex = Math.floor((360 - normalizedRotation + (sliceAngle / 2)) % 360 / sliceAngle);
      setWinner(wheelItems[winningIndex].text);
    }, 4000); // 4 seconds animation
  };

  // SVG Wheel Generation
  const createWheelSlices = () => {
    const numSlices = wheelItems.length;
    const sliceAngle = 360 / numSlices;
    const radius = 200;
    const cx = 200;
    const cy = 200;

    return wheelItems.map((item, i) => {
      const startAngle = i * sliceAngle - 90;
      const endAngle = startAngle + sliceAngle;

      const x1 = cx + radius * Math.cos((Math.PI * startAngle) / 180);
      const y1 = cy + radius * Math.sin((Math.PI * startAngle) / 180);
      const x2 = cx + radius * Math.cos((Math.PI * endAngle) / 180);
      const y2 = cy + radius * Math.sin((Math.PI * endAngle) / 180);

      const largeArcFlag = sliceAngle > 180 ? 1 : 0;
      const pathData = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
      
      const textAngle = startAngle + (sliceAngle / 2);
      const textRadius = radius * 0.65;
      const textX = cx + textRadius * Math.cos((Math.PI * textAngle) / 180);
      const textY = cy + textRadius * Math.sin((Math.PI * textAngle) / 180);

      return (
        <g key={item.id}>
          <path d={pathData} fill={COLORS[i % COLORS.length]} stroke="#fff" strokeWidth="2" />
          <text 
            x={textX} 
            y={textY} 
            fill="#ffffff" 
            fontSize={numSlices > 8 ? "12" : "16"} 
            fontWeight="bold" 
            textAnchor="middle" 
            alignmentBaseline="middle"
            transform={`rotate(${textAngle + 90}, ${textX}, ${textY})`}
            className="drop-shadow-md pointer-events-none select-none"
          >
            {item.text.length > 15 ? item.text.substring(0, 15) + '...' : item.text}
          </text>
        </g>
      );
    });
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 p-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-600 to-rose-900 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-3xl font-black flex items-center mb-2">
            <Trophy className="w-8 h-8 mr-3 text-pink-300" />
            Giant Virtual Prize Wheel & Dice
          </h2>
          <p className="text-pink-100 max-w-xl text-lg">
            High-contrast, huge graphics perfect for the projector or smart TV. Hook up your laptop and run bingo, raffles, and board games so the whole room can see!
          </p>
        </div>
        <div className="flex bg-white/10 p-1 rounded-lg">
          <button 
            onClick={() => setActiveTab('dice')}
            className={cn("px-6 py-2 rounded-md font-bold transition-all", activeTab === 'dice' ? "bg-white text-rose-900 shadow-md" : "hover:bg-white/10")}
          >
            Dice Roller
          </button>
          <button 
            onClick={() => setActiveTab('wheel')}
            className={cn("px-6 py-2 rounded-md font-bold transition-all", activeTab === 'wheel' ? "bg-white text-rose-900 shadow-md" : "hover:bg-white/10")}
          >
            Prize Wheel
          </button>
        </div>
      </div>

      {activeTab === 'dice' && (
        <div className="grid lg:grid-cols-3 gap-8">
          <Card className="p-6 lg:col-span-1 space-y-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
            <h3 className="font-bold uppercase text-slate-500 tracking-wider text-sm flex items-center">
              <Settings className="w-4 h-4 mr-2" /> Dice Settings
            </h3>
            
            <div>
              <label className="block text-sm font-semibold mb-3">Number of Dice</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5, 6].map(num => (
                  <button
                    key={num}
                    onClick={() => setNumDice(num)}
                    className={cn(
                      "w-10 h-10 rounded-lg font-bold transition-all",
                      numDice === num ? "bg-rose-600 text-white shadow-md scale-110" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200"
                    )}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-6 border-t border-slate-100 dark:border-slate-800">
              <Button 
                onClick={rollDice}
                disabled={isRolling}
                className="w-full h-16 text-xl font-black bg-rose-600 hover:bg-rose-700 text-white shadow-lg shadow-rose-500/30"
              >
                <Dices className="w-6 h-6 mr-2" />
                ROLL DICE
              </Button>
            </div>
          </Card>

          <Card className="p-8 lg:col-span-2 border-slate-200 dark:border-slate-800 shadow-xl bg-slate-50 dark:bg-slate-900 min-h-[500px] flex flex-col items-center justify-center relative overflow-hidden">
            <div className="flex flex-wrap justify-center gap-8 items-center max-w-4xl mx-auto z-10">
              <AnimatePresence mode="popLayout">
                {diceValues.map((val, i) => (
                  <motion.div
                    key={i}
                    layout
                    initial={{ scale: 0.5, rotate: -180, opacity: 0 }}
                    animate={{ 
                      scale: 1, 
                      rotate: isRolling ? [0, 90, 180, 270, 360] : 0, 
                      opacity: 1 
                    }}
                    transition={{ 
                      duration: isRolling ? 0.2 : 0.5, 
                      repeat: isRolling ? Infinity : 0,
                      type: isRolling ? 'tween' : 'spring'
                    }}
                    className="w-32 h-32 md:w-48 md:h-48 bg-white dark:bg-slate-800 rounded-3xl shadow-2xl border-4 border-slate-200 dark:border-slate-700 flex items-center justify-center"
                  >
                    <span className="text-7xl md:text-9xl font-black text-slate-900 dark:text-white select-none">
                      {val}
                    </span>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
            
            {!isRolling && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-12 text-3xl md:text-5xl font-black text-slate-400 dark:text-slate-600 uppercase tracking-widest z-10"
              >
                Total: <span className="text-rose-600 dark:text-rose-500">{diceValues.reduce((a, b) => a + b, 0)}</span>
              </motion.div>
            )}
            
            {/* Background decorative elements */}
            <Dices className="absolute -bottom-20 -right-20 w-96 h-96 text-slate-200 dark:text-slate-800/50 -rotate-12 z-0 opacity-50 pointer-events-none" />
          </Card>
        </div>
      )}

      {activeTab === 'wheel' && (
        <div className="grid lg:grid-cols-3 gap-8">
          <Card className="p-6 lg:col-span-1 space-y-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 flex flex-col h-[600px]">
            <h3 className="font-bold uppercase text-slate-500 tracking-wider text-sm flex items-center">
              <Settings className="w-4 h-4 mr-2" /> Wheel Slices
            </h3>
            
            <div className="flex-1 overflow-y-auto pr-2 space-y-2">
              {wheelItems.map((item) => (
                <div key={item.id} className="flex items-center gap-2">
                  <Input 
                    value={item.text} 
                    readOnly
                    className="bg-slate-50 dark:bg-slate-800 font-semibold"
                  />
                  <Button 
                    variant="secondary" 
                    size="icon" 
                    onClick={() => removeWheelItem(item.id)}
                    disabled={wheelItems.length <= 2 || isSpinning}
                    className="shrink-0 text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
              <div className="flex gap-2 mb-4">
                <Input 
                  placeholder="Add prize..." 
                  value={newItemText}
                  onChange={(e) => setNewItemText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addWheelItem()}
                  disabled={isSpinning}
                />
                <Button onClick={addWheelItem} disabled={isSpinning} className="shrink-0 bg-pink-600 hover:bg-pink-700 text-white">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              <Button 
                onClick={spinWheel}
                disabled={isSpinning || wheelItems.length < 2}
                className="w-full h-16 text-xl font-black bg-pink-600 hover:bg-pink-700 text-white shadow-lg shadow-pink-500/30"
              >
                <RefreshCw className={cn("w-6 h-6 mr-2", isSpinning && "animate-spin")} />
                {isSpinning ? 'SPINNING...' : 'SPIN WHEEL'}
              </Button>
            </div>
          </Card>

          <Card className="p-8 lg:col-span-2 border-slate-200 dark:border-slate-800 shadow-xl bg-slate-900 min-h-[600px] flex flex-col items-center justify-center relative overflow-hidden">
            <div className="relative w-full max-w-[500px] aspect-square flex items-center justify-center z-10">
              {/* The Wheel */}
              <div 
                className="relative w-full h-full rounded-full shadow-[0_0_40px_rgba(0,0,0,0.5)] border-8 border-white dark:border-slate-800 overflow-hidden"
                style={{ 
                  transform: `rotate(${rotation}deg)`, 
                  transition: 'transform 4s cubic-bezier(0.175, 0.885, 0.32, 1.05)' 
                }}
              >
                <svg viewBox="0 0 400 400" className="w-full h-full">
                  {createWheelSlices()}
                </svg>
              </div>

              {/* Center Hub */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-white rounded-full shadow-lg border-4 border-slate-200 z-20 flex items-center justify-center">
                <div className="w-4 h-4 bg-slate-900 rounded-full"></div>
              </div>

              {/* The Pointer */}
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 z-30 drop-shadow-xl flex flex-col items-center">
                <div className="w-8 h-12 bg-white rounded-t-md border-2 border-slate-200"></div>
                <div className="w-0 h-0 border-l-[16px] border-l-transparent border-r-[16px] border-r-transparent border-t-[20px] border-t-white drop-shadow-md"></div>
              </div>
            </div>

            {/* Winner Announcement */}
            <AnimatePresence>
              {winner && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.5, y: 50 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  className="absolute inset-0 z-40 bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center text-center p-8"
                >
                  <Trophy className="w-24 h-24 text-yellow-400 mb-6 drop-shadow-[0_0_15px_rgba(250,204,21,0.5)]" />
                  <h3 className="text-2xl font-bold text-white uppercase tracking-widest mb-2">We have a winner!</h3>
                  <div className="text-6xl md:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-yellow-400 drop-shadow-lg">
                    {winner}
                  </div>
                  <Button 
                    onClick={() => setWinner(null)}
                    className="mt-12 text-lg h-12 px-8 bg-white text-black hover:bg-slate-200"
                  >
                    Close
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        </div>
      )}
    </div>
  );
}
